import React, { useCallback, useEffect, useRef, useState } from "react";
import CommonGrid from "../CommonGrid";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer } from "@mui/material";
import NewTrackingReportModal from "./NewTrackingReportModal";
import LightTooltip from "../../../commonComponents/LightTooltip";
import { api } from "../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, debounce } from "lodash";
import ConfirmModal from "../../ItemProperties/Forms/FieldDataValidation/ConfirmModal";

const TrackingReportList = ({
	newTrackingStates,
	setNewTrackingStates,
	trackingReportList,
	setSelectedTR,
	selectedTR,
	fetchTrackingReports,
	setListGridAPI,
	enableApply,
	setEnableApply,
	detailsGridAPI,
	applyHandler,
	isLoading,
	applyLoading : applyButtonLoading,
	listGridAPI,
	setSnackbar,
	disableModification
}) => {
	const { t } = useTranslation();
	const [rowData, setRowData] = useState([]);
	const [gridAPI, setGridAPI] = useState();
	const dispatch = useDispatch();
	const {account, user} = useSelector(state => state.queryParams);
	const nextSelectedTR = useRef(null);
	const [deleteState, setDeleteState] = useState({});
	const [unsavedState, setUnsavedState] = useState({});
	const [applyLoading, setApplyLoading] = useState(false);
	const [copyFields, setCopyFields] = useState([]);
	
	// Debounced snackbar function to prevent rapid successive error messages
	const debouncedSetSnackbar = useCallback(
		debounce((snackbarConfig) => {
			setSnackbar(snackbarConfig);
		}, 300),
		[setSnackbar]
	);
	const customClickHandlerOnAdd = () => {
		setNewTrackingStates({
			copyFields: [],
			isOpen: true,
			isOpenedByNewTR: true
		})
	};
	const onSelectionChanged = useCallback((gridOptions) => {
		nextSelectedTR.current = null;
		const selectedRows = gridOptions.api.getSelectedRows();
		const className = gridOptions.event.target.className;
		if(["DELETE_MUI"].includes(className)) {
			selectTRByName(gridOptions.api, selectedTR?.name);
			return;
		}
		if (["FILE_COPY"].includes(className)) {
			nextSelectedTR.current = {...gridOptions.node.data, buttonClicked: "copy"};
			if (enableApply && !disableModification) {
				setUnsavedState({
					value: selectedTR?.name,
					nextSelection: gridOptions?.node?.data,
					openModal: true,
					message: [...t("LBL_DELETE_DOC_NAME")?.split(`\\n`), gridOptions?.node?.data?.key]?.map((e, i)=> {
						return i === 2 ? <strong>[{e}]</strong> :<p>{e}</p>
					})
				})
			}else {
				copyTrackingPartner(gridOptions.node.data.key, gridOptions.node.data.type);
			}
			return;
		}
		else if (selectedRows.length) {
			if (enableApply && !disableModification) {
				setUnsavedState({
					value: selectedTR?.name,
					nextSelection: gridOptions?.node?.data,
					openModal: true,
					message: [...t("LBL_DELETE_DOC_NAME")?.split(`\\n`), gridOptions?.node?.data?.key]?.map((e, i)=> {
						return i === 2 ? <strong>[{e}]</strong> :<p>{e}</p>
					})
				})
				selectTRByName(gridOptions.api, selectedTR?.name);
				nextSelectedTR.current = gridOptions.node.data;
				return;                                    
			}		
			gridOptions.node.setSelected(true);
			setSelectedTR({name: selectedRows[0]?.key, type: selectedRows[0]?.type}); 
		}
	}, [enableApply, selectedTR, disableModification]);

	const deleteTrackingReportHandler = (reportName, gridAPI, id) => {
		dispatch(api.endpoints.deleteTrackingReport.initiate({
			data : {
				"type": "OTHER",
				"account_name":account,
				"user_name":user,
				"report_name":reportName,
				"fields":[]
			}
		})).then(res => {
			if (res.data.current_state === "SUCCESS") {
				updateTrackingReportListById(gridAPI, id, 0)
				setSnackbar({
					isOpen:true,
					severity:'success',
					message:res.data.message
				})
			}else {
				debouncedSetSnackbar({
					isOpen:true,
					severity:'error',
					message:res.data.message
				})
			}
		})
		.catch((err) => {
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		})
	}

	const copyTrackingPartner = useCallback((reportName, type = "", gridAPI, id) => {
		const createStructuredFields = (fields) => {
			const finalFields = []
			fields.forEach(nodeData => {
				if (nodeData?.hasOwnProperty("id")) delete nodeData?.id;
				if (nodeData?.hasOwnProperty("initialData")) delete nodeData?.initialData;
				if (nodeData.Field_Name) {
					const finalField = {
						"name": nodeData?.Field_Name,
						"data_type":nodeData?.Data_Type,
						"size": nodeData?.Size,
						"set_insert":nodeData?.Is_Insert,
						"set_update":nodeData?.Is_Update,
						"set_key":nodeData?.Is_Key
					};
					finalFields.push(finalField);
				}
			})
			return finalFields;
		}
		dispatch(api.endpoints.getTrackingReportFields.initiate({
			user,
			account,
			reportName
		}, {forceRefetch: true}))
		.then(res => {
			if (res.data.FieldNames) {
				setNewTrackingStates({
					copyFields: createStructuredFields(cloneDeep(res.data.FieldNames)),
					isOpen: true,
					isOpenedByNewTR: false,
					type: type?.toLowerCase() === "edi" ? "edi" : "other",
				})
			}
		})
		.catch((err) => {
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		})
	},[detailsGridAPI, selectedTR?.name])

	const updateTrackingReportListById = (gridAPI, removeId, newId) => {
		if (!gridAPI) return;
		const rowData = [];
		gridAPI.forEachNode(node => {
			if (node.data.id !== removeId) rowData.push(node);
		})
		if (!rowData.length) setSelectedTR({});
		else {
			setSelectedTR({name: rowData?.[newId]?.data?.key, type: rowData?.[newId]?.data?.type});
		}
		gridAPI.setGridOption("rowData", rowData?.map((e, i) => ({...e.data, id: i})));
		selectTRByName(gridAPI, rowData?.[newId]?.data?.key)
	}

	const deleteHandler = useCallback((value, api, id) => {
		if (applyButtonLoading) return;
		setDeleteState({
			value,
			api,
			id,
			openModal: true,
			message: [...t("LBL_DELETE_DOC_NAME")?.split(`\\n`), value]?.map((e, i)=> {
				return i === 2 ? <strong>[{e}]</strong> :<p>{e}</p>
			})
		})
	}, [applyButtonLoading])

	const gridOptions = {
		columnDefs: [
			{
				headerName: t("LBL_TRACKING_REPORT_NAME"),
				field: "key",
				addNewControl: disableModification ? false : true,
				customClickHandlerOnAdd: customClickHandlerOnAdd,
				cellRenderer : CustomCellRenderer,
				addNewControlLabel: t("BTN_NEW_TRACKING_REPORT"),
				cellRendererParams: {
					copyTrackingPartner: copyTrackingPartner,
					enableApply: enableApply,
					deleteHandler: deleteHandler,
					applyLoading: applyButtonLoading,
					disableModification: disableModification
				},
			},
		],
	};

	const selectTRByName = (gridAPI,name) => {
		if (!gridAPI) return;
		gridAPI.forEachNode((node) => {
			if (node.data.key === name) {
				node.setSelected(true);
				gridAPI.ensureNodeVisible(node, "middle"); 
			}
		})
	}

	useEffect(() => {
		if (gridAPI && disableModification) {
			gridAPI.setGridOption("columnDefs", [
				{
					headerName: t("LBL_TRACKING_REPORT_NAME"),
					field: "key",
					addNewControl: disableModification ? false : true,
					customClickHandlerOnAdd: customClickHandlerOnAdd,
					cellRenderer : CustomCellRenderer,
					addNewControlLabel: t("BTN_NEW_TRACKING_REPORT"),
					cellRendererParams: {
						copyTrackingPartner: copyTrackingPartner,
						enableApply: enableApply,
						deleteHandler: deleteHandler,
						applyLoading: applyButtonLoading,
						disableModification: disableModification
					},
				},
			])
		}
	}, [disableModification, gridAPI])


	useEffect(() => {
		// Sort tracking reports alphabetically by name before mapping to rowData
		const sortedTrackingReports = [...(trackingReportList || [])].sort((a, b) => {
			const nameA = a.name?.toLowerCase() || '';
			const nameB = b.name?.toLowerCase() || '';
			return nameA.localeCompare(nameB);
		});
		
		setRowData(sortedTrackingReports.map((e, i)=> ({key: e.name, id: i, type: e.type})));
	},[trackingReportList])


	// This UseEffect is for selecting correct key from tracking reports
	useEffect(() => {
		if (selectedTR?.name && gridAPI) {
			const selectedKeys = gridAPI.getSelectedRows();
			if (!selectedKeys || (selectedKeys?.[0] !== selectedTR.name)) {
				selectTRByName(gridAPI, selectedTR?.name)
			}
		}
	},[selectedTR.name, gridAPI, rowData, nextSelectedTR.current])

	return (
		<div>
			<CommonGrid
				isLoading={isLoading}
				rowData={rowData}
				gridOptions={gridOptions}
				containerClasses='trading-partner-grids'
				getGridAPI={(e, gridRef) => {
					setGridAPI(gridRef.api);
					setListGridAPI(gridRef.api);
				}}
				gridProps={{
					rowSelection: "single",
					onRowClicked: onSelectionChanged,
				}}
				
			/>
			<NewTrackingReportModal 
				fetchTrackingReports={fetchTrackingReports}  
				newTrackingStates={newTrackingStates}
				setNewTrackingStates={setNewTrackingStates}
				listGridAPI={listGridAPI}
				setSnackbar={setSnackbar}
			/>
			<ConfirmModal
				openModal={deleteState?.openModal || false}
				message={deleteState?.message || ""}
				handleClose={() => {
					setDeleteState(prev => ({}))
				}}
				yesHandler={() => {
					deleteTrackingReportHandler(deleteState.value, deleteState.api, deleteState.id);
					setDeleteState({});
				}}
				noHandler={() => setDeleteState(prev => ({}))}
			/>
			<ConfirmApplyModal
				open={unsavedState?.openModal || false}
				tr={unsavedState.value}
				applyHandler={() => {
					setApplyLoading(true);
					applyHandler(() => {
						setSelectedTR({name: unsavedState.nextSelection.key, type: unsavedState.nextSelection.type})
						setUnsavedState({});
						setApplyLoading(false);
						if (nextSelectedTR.current) {
							if (nextSelectedTR.current.buttonClicked === "copy") {
								copyTrackingPartner(nextSelectedTR.current.key, nextSelectedTR.current.type);
							}
						}
					})
				}}
				handleApplyCancel={() => {
					setEnableApply(false);
					setSelectedTR({});
					setTimeout(() => {
						setSelectedTR({name: unsavedState.nextSelection.key, type: unsavedState.nextSelection.type})
						setUnsavedState(prev => ({}))
						if (nextSelectedTR.current) {
							if (nextSelectedTR.current.buttonClicked === "copy") {
								copyTrackingPartner(nextSelectedTR.current.key, nextSelectedTR.current.type);
							}
						}
					})
				}}
				applyLoading={applyLoading}
			/>
		</div>
	);
};

export default TrackingReportList;


const CustomCellRenderer = (props) => {
	const { value, deleteHandler, copyTrackingPartner, api, data, applyLoading, disableModification, enableApply } = props;
	const { t } = useTranslation();
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				height: "2rem",
			}}
		>
			{!value || /^\s*$/.test(value) ? (
				<span style={{ color: "#0000008A" }}>{t("LBL_NEW_PDC")}</span>
			) : (
				<div className='truncated-text' title={value}>
					{value}
				</div>
			)}
			{value && !disableModification ? (
				<div className={'flex align-center' + (applyLoading ? "disable-element" : "")}>
					<LightTooltip title={t("BTN_DEL_TRACKING_REPORT")} arrow>
						<span
							style={{ cursor: "pointer" }}
							className='DELETE_MUI'
							onClick={(e) => {
								e.stopPropagation();
								deleteHandler(value, api, data.id)
							}}
						></span>
					</LightTooltip>
					<LightTooltip title={t("BTN_COPY_TRACKING_REPORT")} arrow>
						<span
							style={{ cursor: "pointer" }}
							className='FILE_COPY'
							onClick={(e) => {
								e.stopPropagation();
								// copyTrackingPartner(value, data.type, api, data.id)
							}}
						></span>
					</LightTooltip>
				</div>
			) : (
				""
			)}
		</div>
	);
};

export const ConfirmApplyModal = ({ open, tr, applyHandler, handleApplyCancel, applyLoading }) => {
	const { t } = useTranslation();
	return <>
	  <Dialog
		open={open}
		aria-labelledby="alert-dialog-title"
		aria-describedby="alert-dialog-description"
	  >
		<DialogTitle className='dialog-header'>
		  <div className="title">
			{t("BOX_CONFIRM")}
		  </div>
		</DialogTitle>
  
		<DialogContent className='modal-content' dividers>
		  {t('THERE_ARE_UNSAVED_CHANGES_MADE_TO')?.split('{0}').join(' ')} - <span className="bold">{tr}</span>
		</DialogContent>
  
		<DialogActions>
		  <Button
			size='small'
			variant="outlined"
			color="primary"
			onClick={applyHandler}>
			{applyLoading ? <CircularProgress size={"18px"}/> : t("APPLY")}
		  </Button>
		  <Button
			size='small'
			variant="outlined"
			color="primary"
			onClick={handleApplyCancel}>
			{t("BTN_CANCEL")}
		  </Button>
		</DialogActions>
	  </Dialog>
	</>
}
